import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { dashboardAPI } from '../api/client';
import { Car, Bell, Wallet, AlertTriangle, Plus, Calendar, TrendingUp, FileText, Wrench, Zap, ArrowRight, Mic } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const subscription = user?.subscription || {};
  const tier = subscription.tier || 'free';
  const isFreeTier = tier === 'free';

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await dashboardAPI.get();
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getReminderStatus = (dueDate) => {
    const days = differenceInDays(parseISO(dueDate), new Date());
    if (days < 0) return { label: 'Overdue', className: 'status-overdue' };
    if (days <= 7) return { label: 'Due Soon', className: 'status-due-soon' };
    return { label: 'OK', className: 'status-ok' };
  };

  const pageVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  const stats = [
    {
      title: 'Active Vehicles',
      value: dashboard?.vehicle_count || 0,
      icon: Car,
      color: 'text-[hsl(var(--brand-navy))]',
      bgColor: 'bg-[hsl(var(--brand-navy))]/10',
      testId: 'vehicle-count'
    },
    {
      title: 'Upcoming Reminders',
      value: dashboard?.upcoming_reminders?.length || 0,
      icon: Bell,
      color: 'text-[hsl(var(--brand-tangerine))]',
      bgColor: 'bg-[hsl(var(--brand-tangerine))]/10',
      testId: 'reminder-count'
    },
    {
      title: 'Monthly Expenses',
      value: `£${dashboard?.monthly_expenses?.toFixed(2) || '0.00'}`,
      icon: Wallet,
      color: 'text-[hsl(var(--brand-teal))]',
      bgColor: 'bg-[hsl(var(--brand-teal))]/10',
      testId: 'expense-total'
    },
    {
      title: 'Total Documents',
      value: dashboard?.document_count || 0,
      icon: FileText,
      color: 'text-[hsl(var(--brand-sky))]',
      bgColor: 'bg-[hsl(var(--brand-sky))]/10',
      testId: 'document-count'
    }
  ];

  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={{ duration: 0.3 }}
      className="space-y-6 pb-6"
    >
      {/* Hero Header with Gradient */}
      <div className="hero-gradient texture-noise rounded-2xl p-8 md:p-10 -mx-4 sm:mx-0">
        <div className="relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl font-semibold tracking-tight text-foreground"
          >
            Dashboard
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-muted-foreground mt-2 text-base sm:text-lg"
          >
            Welcome back! Here's your vehicle overview
          </motion.p>

          {/* Emergency Button */}
          <Link to="/app/incidents/record">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="mt-4"
            >
              <Button 
                size="lg" 
                className="bg-red-500 hover:bg-red-600 text-white gap-2 shadow-lg"
                data-testid="emergency-record-button"
              >
                <Mic className="h-5 w-5" />
                🚨 Record Emergency Incident
              </Button>
            </motion.div>
          </Link>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-3 mt-6"
          >
            <Button 
              asChild 
              size="lg" 
              className="rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              data-testid="add-vehicle-button"
            >
              <Link to="/app/vehicles/add">
                <Plus className="h-5 w-5 mr-2" />
                Add Vehicle
              </Link>
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              asChild 
              className="rounded-xl bg-background/80 backdrop-blur"
              data-testid="report-accident-button"
            >
              <Link to="/app/accidents/new">
                <AlertTriangle className="h-5 w-5 mr-2" />
                Report Accident
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Stats Cards with Animation */}
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
      >
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div key={stat.title} variants={cardVariants}>
              <Card className="card-elevated card-hover border-none">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </CardTitle>
                  <div className={`${stat.bgColor} ${stat.color} p-2.5 rounded-xl`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div 
                    className="text-3xl font-semibold tracking-tight counter-number" 
                    data-testid={stat.testId}
                  >
                    {stat.value}
                  </div>
                  {stat.subtitle && (
                    <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <h2 className="text-2xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="card-hover cursor-pointer" data-testid="quick-action-scan">
            <Link to="/app/scan" className="block">
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--scan-soft))] text-[hsl(var(--scan))] flex items-center justify-center mb-3">
                  <FileText className="h-6 w-6" />
                </div>
                <CardTitle className="text-lg">Scan Document</CardTitle>
                <CardDescription>
                  Use AI to extract vehicle details from V5C or insurance docs
                </CardDescription>
              </CardHeader>
            </Link>
          </Card>

          <Card className="card-hover cursor-pointer" data-testid="quick-action-expense">
            <Link to="/app/expenses" className="block">
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand-teal))]/10 text-[hsl(var(--brand-teal))] flex items-center justify-center mb-3">
                  <Wallet className="h-6 w-6" />
                </div>
                <CardTitle className="text-lg">Add Expense</CardTitle>
                <CardDescription>
                  Track fuel, maintenance, insurance and other vehicle costs
                </CardDescription>
              </CardHeader>
            </Link>
          </Card>

          <Card className="card-hover cursor-pointer" data-testid="quick-action-maintenance">
            <Link to="/app/maintenance" className="block">
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand-sky))]/10 text-[hsl(var(--brand-sky))] flex items-center justify-center mb-3">
                  <Wrench className="h-6 w-6" />
                </div>
                <CardTitle className="text-lg">Log Service</CardTitle>
                <CardDescription>
                  Keep your digital service book up-to-date with maintenance records
                </CardDescription>
              </CardHeader>
            </Link>
          </Card>

          {/* Upgrade CTA - Only show for free tier */}
          {isFreeTier && (
            <Card className="card-hover cursor-pointer bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20" data-testid="quick-action-upgrade">
              <Link to="/pricing" className="block">
                <CardHeader>
                  <div className="w-12 h-12 rounded-xl bg-primary/20 text-primary flex items-center justify-center mb-3">
                    <Zap className="h-6 w-6" />
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-lg">Upgrade to Premium</CardTitle>
                    <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                      £4.99/mo
                    </Badge>
                  </div>
                  <CardDescription>
                    Unlock unlimited vehicles, AI scans, and premium features
                  </CardDescription>
                  <div className="flex items-center gap-2 mt-3 text-sm text-primary font-medium">
                    <span>View Plans</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </CardHeader>
              </Link>
            </Card>
          )}
        </div>
      </motion.div>

      {/* Upcoming Reminders */}
      {dashboard?.upcoming_reminders && dashboard.upcoming_reminders.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold">Upcoming Reminders</h2>
            <Button variant="ghost" asChild>
              <Link to="/app/reminders">View All</Link>
            </Button>
          </div>
          <div className="grid gap-4" data-testid="reminders-list">
            {dashboard.upcoming_reminders.slice(0, 3).map((reminder, index) => {
              const status = getReminderStatus(reminder.due_date);
              return (
                <motion.div
                  key={reminder.reminder_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  <Card className="card-hover">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <CardTitle className="text-base">{reminder.title}</CardTitle>
                          </div>
                          <CardDescription className="mt-1">
                            Due: {format(parseISO(reminder.due_date), 'dd MMM yyyy')}
                          </CardDescription>
                        </div>
                        <Badge className={status.className}>
                          {status.label}
                        </Badge>
                      </div>
                    </CardHeader>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Recent Vehicles */}
      {dashboard?.recent_vehicles && dashboard.recent_vehicles.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold">Your Vehicles</h2>
            <Button variant="ghost" asChild>
              <Link to="/app/vehicles">View All</Link>
            </Button>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="vehicles-list">
            {dashboard.recent_vehicles.map((vehicle, index) => (
              <motion.div
                key={vehicle.vehicle_id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 + index * 0.1 }}
              >
                <Card className="card-elevated card-hover cursor-pointer">
                  <Link to={`/app/vehicles/${vehicle.vehicle_id}`}>
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                          <Car className="h-6 w-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg truncate">
                            {vehicle.make} {vehicle.model}
                          </CardTitle>
                          <CardDescription className="font-mono text-sm">
                            {vehicle.registration}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                  </Link>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Empty State */}
      {!dashboard?.vehicle_count && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="card-elevated text-center py-12">
            <CardHeader>
              <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Car className="h-10 w-10" />
              </div>
              <CardTitle className="text-2xl">No vehicles yet</CardTitle>
              <CardDescription className="text-base mt-2 max-w-md mx-auto">
                Get started by adding your first vehicle. Track maintenance, expenses, and important reminders all in one place.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild size="lg" className="rounded-xl">
                <Link to="/app/vehicles/add">
                  <Plus className="h-5 w-5 mr-2" />
                  Add Your First Vehicle
                </Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Dashboard;
